import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import type { Account } from '../../types'
import { ACCOUNTS_CHANGED } from '../shared/events'

/**
 * Mirrors the legacy `loadAccounts()` at index.html:1736 — same select
 * columns, same search/industry filters, same pagination size and ordering.
 * Adds a contacts-count map by re-querying the contacts table for the
 * current page's account ids.
 *
 * Re-fetches automatically on `ACCOUNTS_CHANGED` events from the bus, so
 * the legacy save/cleanup modals can keep ownership of mutations.
 */

export const ACCOUNTS_PAGE_SIZE = 50

export interface AccountsRow extends Pick<
  Account,
  | 'id'
  | 'company_name'
  | 'company_domain'
  | 'industry'
  | 'hq_country'
  | 'employee_count'
  | 'funding_stage'
  | 'icp_score'
> {
  contacts_count: number
}

export interface UseAccountsArgs {
  search: string
  industry: string
  page: number
}

export interface UseAccountsResult {
  rows: AccountsRow[]
  total: number
  status: 'idle' | 'loading' | 'success' | 'error'
  error: string | null
  refetch: () => void
}

export function useAccounts({ search, industry, page }: UseAccountsArgs): UseAccountsResult {
  const [rows, setRows] = useState<AccountsRow[]>([])
  const [total, setTotal] = useState(0)
  const [status, setStatus] = useState<UseAccountsResult['status']>('idle')
  const [error, setError] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)

  const refetch = useCallback(() => setRefreshKey((k) => k + 1), [])

  // Subscribe to the legacy → island event channel.
  useEffect(() => {
    const bus = window.ShaunIslands?.events
    if (!bus) return
    const handler = () => refetch()
    bus.addEventListener(ACCOUNTS_CHANGED, handler)
    return () => bus.removeEventListener(ACCOUNTS_CHANGED, handler)
  }, [refetch])

  useEffect(() => {
    let cancelled = false
    setStatus('loading')
    setError(null)
    void (async () => {
      try {
        const from = (page - 1) * ACCOUNTS_PAGE_SIZE
        const to = from + ACCOUNTS_PAGE_SIZE - 1

        let q = supabase
          .from('accounts')
          .select(
            'id,company_name,company_domain,industry,hq_country,employee_count,funding_stage,icp_score',
            { count: 'exact' },
          )
          .range(from, to)
          .order('company_name', { ascending: true })

        const trimmed = search.trim()
        if (trimmed) {
          q = q.or(`company_name.ilike.%${trimmed}%,company_domain.ilike.%${trimmed}%`)
        }
        if (industry) q = q.ilike('industry', `%${industry}%`)

        const { data, count, error: queryError } = await q
        if (cancelled) return
        if (queryError) {
          setStatus('error')
          setError(queryError.message)
          setRows([])
          setTotal(0)
          return
        }

        const accountRows = (data ?? []) as Array<Omit<AccountsRow, 'contacts_count'>>
        const ids = accountRows.map((r) => r.id)

        let contactsCount: Record<string, number> = {}
        if (ids.length > 0) {
          const { data: cData } = await supabase
            .from('contacts')
            .select('account_id')
            .in('account_id', ids)
          if (cancelled) return
          contactsCount = (cData ?? []).reduce<Record<string, number>>((acc, row) => {
            const id = (row as { account_id: string | null }).account_id
            if (id) acc[id] = (acc[id] ?? 0) + 1
            return acc
          }, {})
        }

        setRows(accountRows.map((r) => ({ ...r, contacts_count: contactsCount[r.id] ?? 0 })))
        setTotal(count ?? 0)
        setStatus('success')
      } catch (e) {
        if (cancelled) return
        setStatus('error')
        setError(e instanceof Error ? e.message : String(e))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [search, industry, page, refreshKey])

  return { rows, total, status, error, refetch }
}
